import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { DbVersionInfo, V2Schedule } from "../../models";
import {
  importXer,
  fetchV2Schedules,
  setDataDate,
  renameSchedule,
} from "../../api/clientV2";
import { useClickOutside } from "../../hooks/useClickOutside";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { formatDataDate, formatDataDateShort, truncate } from "../../utils/format";
import "./DbSwitcher.css";

/** Max length of a user-set display label, and the dropdown truncation width. */
const NAME_MAX = 15;

/** The effective display name: the user's custom label if set, otherwise the
 *  P6 project (WBS-root) name. `schedule_name` is still a raw `xer_*` stem when
 *  the user hasn't renamed it. */
function displayName(v: {
  schedule_name: string;
  project_name: string | null;
}): string {
  const custom = v.schedule_name && !v.schedule_name.startsWith("xer_");
  return (custom ? v.schedule_name : v.project_name || v.schedule_name) || "";
}

/** One dropdown row: "Name — v12 · 06/17/26". */
function optionLabel(v: DbVersionInfo): string {
  const dd = formatDataDateShort(v.original_data_date ?? v.data_date);
  return `${truncate(displayName(v), NAME_MAX)} — v${v.version}${dd ? ` · ${dd}` : ""}`;
}

interface Props {
  versions: DbVersionInfo[];
  activeVersion: number | null;
  onVersionChange: (version: number) => void;
  onImportComplete: (version: number, versions: DbVersionInfo[]) => void;
  onRenamed: (newName: string) => void;
  /** A data date was saved — the host patches its versions list so the header
   *  stays correct across version switches without a refetch. */
  onDataDateSaved: (newDate: string | null) => void;
}

const DbSwitcher: React.FC<Props> = ({
  versions,
  activeVersion,
  onVersionChange,
  onImportComplete,
  onRenamed,
  onDataDateSaved,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);

  // Manually set the project data date (status date). The data date is the
  // forward pass's floor for remaining work and the cap for completed actuals,
  // so advancing it is how the schedule-update cycle runs without re-importing
  // an XER (an imported XER's data date can lag the actual progress in it).
  const [activeSchedule, setActiveSchedule] = useState<V2Schedule | null>(null);
  const scheduleId = activeSchedule?.id ?? null;
  const [ddInput, setDdInput] = useState("");
  // True only while ddInput holds a value the USER typed (vs a prop resync) —
  // the commit effect requires it, so prop changes can never trigger a write.
  const [ddDirty, setDdDirty] = useState(false);
  const [savingDd, setSavingDd] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  // Custom version picker — a button that hugs the SELECTED label (a native
  // <select> always sizes to the widest option, leaving dead space).
  const [selectOpen, setSelectOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const optionsListRef = useRef<HTMLUListElement>(null);
  const activeOptionRef = useRef<HTMLLIElement>(null);

  // Open the version list centered on the active version (long lists otherwise
  // open scrolled to the top, hiding the current selection).
  useLayoutEffect(() => {
    if (!selectOpen) return;
    const list = optionsListRef.current;
    const opt = activeOptionRef.current;
    if (!list || !opt) return;
    list.scrollTop = opt.offsetTop - list.clientHeight / 2 + opt.offsetHeight / 2;
  }, [selectOpen]);

  // Rename the active schedule's display label. The P6 project name (from the
  // imported XER) is shown read-only and never edited.
  const [renameOpen, setRenameOpen] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [savingName, setSavingName] = useState(false);
  const renameRef = useRef<HTMLDivElement>(null);

  // The versions prop is the single source for the displayed data date — a
  // save patches it via onDataDateSaved, so it survives version switches.
  const activeDataDate =
    versions.find((v) => v.version === activeVersion)?.data_date ?? null;

  // Closing the picker cancels any date typed but not yet committed: the input
  // resyncs to the live value and the dirty flag drops, so the pending debounce
  // can't commit after the popover is gone.
  const closePicker = useCallback(() => {
    setPickerOpen(false);
    setDdDirty(false);
    setDdInput(activeDataDate ? activeDataDate.slice(0, 10) : "");
  }, [activeDataDate]);

  // Close popovers on outside click (shared hook) or Escape.
  useClickOutside(pickerRef, closePicker, pickerOpen);
  useClickOutside(renameRef, () => setRenameOpen(false), renameOpen);
  useClickOutside(selectRef, () => setSelectOpen(false), selectOpen);
  useEffect(() => {
    if (!pickerOpen && !renameOpen && !selectOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closePicker();
        setRenameOpen(false);
        setSelectOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [pickerOpen, renameOpen, selectOpen, closePicker]);

  useEffect(() => {
    let cancelled = false;
    // `/schedules` is resolved against the active version server-side, and each
    // versioned DB holds exactly one schedule — so schedules[0] IS the schedule
    // whose data_date the trigger shows. Re-fetched on version switch so it
    // tracks the active DB.
    fetchV2Schedules()
      .then((schedules) => {
        if (!cancelled) setActiveSchedule(schedules[0] ?? null);
      })
      .catch(() => {
        /* leave activeSchedule null — controls stay disabled */
      });
    return () => {
      cancelled = true;
    };
  }, [activeVersion]);

  // Any non-user change to the live date (version switch, import) resyncs the
  // input and clears the dirty flag — a commit can only ever come from typing.
  useEffect(() => {
    setDdInput(activeDataDate ? activeDataDate.slice(0, 10) : "");
    setDdDirty(false);
  }, [activeDataDate]);

  // Applies once the user settles on a date — no separate confirm. A native
  // date input reports a complete value on EVERY segment keystroke (the field
  // is prefilled), so the commit waits for the debounced value to catch up to
  // the input; half-typed years ("0002-…" while typing 2026) and the unchanged
  // current date are skipped. The latest request wins (ddSeq) and its response
  // is applied as server truth; savingDd reflects requests actually in flight.
  const debouncedDd = useDebouncedValue(ddInput, 600);
  const ddSeq = useRef(0);
  const ddInFlight = useRef(0);
  useEffect(() => {
    if (!ddDirty || scheduleId == null) return;
    if (debouncedDd !== ddInput) return;
    if (!debouncedDd || debouncedDd < "2000-01-01") return;
    if (debouncedDd === (activeDataDate ?? "").slice(0, 10)) {
      setDdDirty(false);
      return;
    }
    const seq = ++ddSeq.current;
    ddInFlight.current += 1;
    setSavingDd(true);
    setDataDate(scheduleId, debouncedDd)
      .then((res) => {
        if (seq !== ddSeq.current) return;
        // No page reload — the host patches its versions list; data-date-
        // relative views (overdue review) refetch via the dataDateChanged
        // channel.
        onDataDateSaved(res.data_date ?? debouncedDd);
        setDdDirty(false);
        setPickerOpen(false);
      })
      .catch((err) => {
        console.error("Set data date failed:", err);
        if (seq === ddSeq.current) {
          alert("Setting data date failed. Check console for details.");
        }
      })
      .finally(() => {
        ddInFlight.current -= 1;
        if (ddInFlight.current === 0) setSavingDd(false);
      });
  }, [ddDirty, debouncedDd, ddInput, scheduleId, activeDataDate, onDataDateSaved]);

  const openRename = () => {
    // Prefill the current custom label; a raw `xer_*` import name isn't a real
    // label, so start empty and let the placeholder show the P6 name.
    const current = activeSchedule?.name ?? "";
    setNameInput(current.startsWith("xer_") ? "" : current);
    setRenameOpen((o) => !o);
  };

  const handleRename = async () => {
    if (scheduleId == null) return;
    const trimmed = nameInput.trim();
    if (!trimmed) return;
    setSavingName(true);
    try {
      const res = await renameSchedule(scheduleId, trimmed);
      setActiveSchedule((s) => (s ? { ...s, name: res.name } : s));
      onRenamed(res.name);
      setRenameOpen(false);
    } catch (err) {
      console.error("Rename failed:", err);
      alert("Renaming the schedule failed. Check console for details.");
    } finally {
      setSavingName(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const result = await importXer(file);
      // Fetch updated version list by triggering parent refresh
      onImportComplete(result.version, [
        ...versions,
        {
          version: result.version,
          filename: result.filename,
          schedule_name: result.schedule_name,
          project_name: result.project_name,
          data_date: result.data_date,
          original_data_date: result.original_data_date ?? result.data_date,
        },
      ]);
    } catch (err) {
      console.error("Import failed:", err);
      alert("XER import failed. Check console for details.");
    } finally {
      setImporting(false);
      // Reset file input so the same file can be re-selected
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const active = versions.find((v) => v.version === activeVersion) ?? null;

  return (
    <div className="db-switcher">
      <div className="db-switcher-schedule" ref={renameRef}>
        <div className="db-switcher-selectbox" ref={selectRef}>
          <button
            type="button"
            className={`db-switcher-select${selectOpen ? " is-open" : ""}`}
            onClick={() => setSelectOpen((o) => !o)}
            disabled={versions.length === 0}
            aria-haspopup="listbox"
            aria-expanded={selectOpen}
          >
            <span className="db-switcher-select-label">
              {active ? optionLabel(active) : "No databases"}
            </span>
            <span className="db-switcher-select-caret" aria-hidden="true">⌄</span>
          </button>
          {selectOpen && (
            <ul className="db-switcher-options" role="listbox" ref={optionsListRef}>
              {versions.map((v) => (
                <li
                  key={v.version}
                  role="option"
                  aria-selected={v.version === activeVersion}
                  ref={v.version === activeVersion ? activeOptionRef : undefined}
                >
                  <button
                    type="button"
                    className={`db-switcher-option${v.version === activeVersion ? " is-active" : ""}`}
                    onClick={() => {
                      onVersionChange(v.version);
                      setSelectOpen(false);
                    }}
                  >
                    {optionLabel(v)}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          className={`db-switcher-rename-trigger${renameOpen ? " is-open" : ""}`}
          onClick={openRename}
          disabled={scheduleId == null}
          title="Rename this schedule (display label only — the P6 name is preserved)"
          aria-label="Rename schedule"
        >
          ✎
        </button>
        {renameOpen && (
          <div className="db-switcher-rename-pop">
            <label className="db-switcher-rename-poplabel">Rename schedule</label>
            <input
              type="text"
              className="db-switcher-rename-input"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRename();
              }}
              disabled={savingName}
              maxLength={NAME_MAX}
              placeholder={activeSchedule?.project_name ?? "Schedule name"}
              autoFocus
            />
            <span className="db-switcher-rename-count">
              {nameInput.length}/{NAME_MAX}
            </span>
            {activeSchedule?.project_name && (
              <div className="db-switcher-rename-p6">
                <span className="db-switcher-rename-p6label">P6 name</span>
                <span className="db-switcher-rename-p6val">
                  {activeSchedule.project_name}
                </span>
              </div>
            )}
            <div className="db-switcher-rename-actions">
              <button
                className="db-switcher-rename-cancel"
                onClick={() => setRenameOpen(false)}
                disabled={savingName}
              >
                Cancel
              </button>
              <button
                className="db-switcher-rename-save"
                onClick={handleRename}
                disabled={savingName || !nameInput.trim()}
              >
                {savingName ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}
      </div>
      <button
        className="db-switcher-import"
        onClick={() => fileRef.current?.click()}
        disabled={importing}
      >
        {importing ? "Importing..." : "Import XER"}
      </button>
      <div className="db-switcher-datadate" ref={pickerRef}>
        <button
          className={`db-switcher-datadate-trigger${pickerOpen ? " is-open" : ""}`}
          onClick={() => (pickerOpen ? closePicker() : setPickerOpen(true))}
          disabled={scheduleId == null}
          title="Set the project data date (status date)"
        >
          <span className="db-switcher-datadate-cal" aria-hidden="true">🗓</span>
          Data date
          <b>{formatDataDate(activeDataDate) ?? "—"}</b>
        </button>
        {pickerOpen && (
          <div className="db-switcher-datadate-pop">
            <label className="db-switcher-datadate-poplabel">Set data date</label>
            <div className="db-switcher-datadate-poprow">
              <input
                type="date"
                className="db-switcher-datadate-input"
                value={ddInput}
                onChange={(e) => {
                  setDdInput(e.target.value);
                  setDdDirty(true);
                }}
                autoFocus
              />
              {savingDd && (
                <span className="db-switcher-datadate-saving">Applying…</span>
              )}
            </div>
          </div>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept=".xer"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
    </div>
  );
};

export default DbSwitcher;
